import React from "react";
import {
  RefreshCcw,
  FileText,
  CreditCard,
  ShieldCheck,
  AlertTriangle,
  Phone,
  Mail,
} from "lucide-react";
import Footer from "../Footer";import { useTranslation } from 'react-i18next';


/* ---------- HELPERS ---------- */

const Highlight = ({ children }) => (
  <span className="font-semibold text-purple-700">{children}</span>
);

const Divider = () => (
  <div className="my-16 h-[1px] w-full bg-gradient-to-r from-transparent via-purple-200 to-transparent" />
);

const Section = ({ title, icon: Icon, children }) => (
  <section>
    <div className="flex items-start gap-4 mb-6">
      <div className="w-11 h-11 rounded-xl bg-purple-100 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-purple-600" />
      </div>

      <h2
        className="
          text-2xl md:text-3xl font-semibold
          bg-gradient-to-r from-purple-700 via-purple-500 to-indigo-600
          bg-clip-text text-transparent
        "
      >
        {title}
      </h2>
    </div>

    <div className="pl-[3.25rem] text-gray-700 leading-relaxed space-y-3">
      {children}
    </div>
  </section>
);

/* ---------- PAGE ---------- */

const RefundCancellationPolicy = () => {
  return (
    <>

      <main className="relative bg-white overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-50 via-white to-white pointer-events-none" />

        <div className="relative container mx-auto px-6 py-24 max-w-7xl mt-6">

          {/* HERO */}
          <header className="mb-16">
            <p
              className="
                inline-block
                text-sm uppercase tracking-widest font-semibold
                text-purple-700
                px-4 py-1.5 rounded-full
                bg-gradient-to-r
                from-purple-100 via-purple-50 to-indigo-100
                mb-4
              "
            >
              Legal & Compliance
            </p>

            <h1
              className="
                text-4xl md:text-5xl font-semibold leading-tight
                bg-gradient-to-r
                from-pink-600 via-purple-600 to-indigo-600
                bg-clip-text text-transparent pb-2
              "
            >
              Refund & Cancellation Policy
            </h1>

            <p className="mt-6 text-gray-600 max-w-4xl text-lg">
              This policy explains how <Highlight>DigiQin</Highlight> handles
              refunds, cancellations, and subscription-related payments for all
              its software products and services.
            </p>
          </header>

          {/* CONTENT SECTIONS */}

          <Section title="Subscription-Based Services" icon={CreditCard}>
            <ul className="list-disc pl-6 space-y-2">
              <li>DigiQin operates on a <Highlight>subscription-based model</Highlight></li>
              <li>Subscription fees are billed in advance</li>
              <li>Once activated, <Highlight>subscriptions are non-refundable</Highlight></li>
              <li>No refunds for partial usage or unused periods</li>
              <li>No refunds for early cancellation by customers</li>
            </ul>
          </Section>

          <Divider />

          <Section title="Trial & Demo Access" icon={FileText}>
            <ul className="list-disc pl-6 space-y-2">
              <li>Free trials or demos may be provided for evaluation</li>
              <li>Customers are advised to test before purchasing</li>
              <li>No refunds after subscription activation post trial/demo</li>
            </ul>
          </Section>

          <Divider />

          <Section title="One-Time Services" icon={RefreshCcw}>
            <p>Refunds are <Highlight>not applicable</Highlight> once work has started for:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Website development</li>
              <li>Digital marketing services</li>
              <li>Custom software development</li>
              <li>Setup or onboarding services</li>
            </ul>
          </Section>

          <Divider />

          <Section title="Exceptions" icon={AlertTriangle}>
            <p>Refunds may be considered only in exceptional cases:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Duplicate payments</li>
              <li>Technical payment errors</li>
              <li>Service not activated due to DigiQin’s fault</li>
            </ul>
            <p>
              Any refund approval is at the <Highlight>sole discretion of DigiQin</Highlight>.
            </p>
          </Section>

          <Divider />

          <Section title="Refund Process (If Applicable)" icon={ShieldCheck}>
            <ul className="list-disc pl-6 space-y-2">
              <li>Approved refunds processed within <Highlight>7–14 business days</Highlight></li>
              <li>Refunds issued via original payment method</li>
              <li>Taxes, gateway fees, or third-party charges may be non-refundable</li>
            </ul>
          </Section>

          <Divider />

          <Section title="Cancellation Policy" icon={RefreshCcw}>
            <ul className="list-disc pl-6 space-y-2">
              <li>Subscriptions can be cancelled at any time</li>
              <li>Cancellation stops future billing</li>
              <li>Access continues until current billing cycle ends</li>
              <li>No refunds for remaining period</li>
            </ul>
          </Section>

          <Divider />

          <Section title="Pricing & Plan Changes" icon={CreditCard}>
            <ul className="list-disc pl-6 space-y-2">
              <li>DigiQin may modify pricing plans</li>
              <li>Features and billing cycles may change</li>
              <li>Advance communication provided where applicable</li>
            </ul>
          </Section>

          <Divider />

          <Section title="Contact for Refund Queries" icon={Phone}>
            <p className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-purple-600" />
              <Highlight>support@digiqin.com</Highlight>
            </p>
            <p className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-purple-600" />
              <Highlight>98925 75520</Highlight>
            </p>
            <p>
              🌐 <Highlight>https://www.digiqin.com</Highlight>
            </p>
          </Section>

          <Divider />

          <Section title="Governing Law" icon={ShieldCheck}>
            <p>
              This policy is governed by the laws of <Highlight>India</Highlight> and
              aligns with applicable consumer and IT regulations.
            </p>
          </Section>

        </div>
      </main>

      <Footer />
    </>
  );
};

export default RefundCancellationPolicy;
